import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "react-bootstrap/Nav";
import { FiPlus } from "react-icons/fi";
import { BiSearch } from "react-icons/bi";
import ReactPaginate from "react-paginate";
import { InAppTemplate } from "../../../shared/templates/inapp";
import { LoadingItem } from "../../../shared/components/loading";
import { AlertMsg } from "../../../shared/components/alert-msg";
import { ConfirmationModal, ErrorModal, SuccessModal } from "../../../shared/components/modals";
import BlogPostRow from "./components/BlogPostRow";
import { BlogService } from "../../../services/blogService";
import { adaptBlogPostFromApi, adaptBlogPostToUpdateCommand, UIBlogPost } from "../../../services/blogAdapters";
import { BlogCategoryDto, BlogPostStatus } from "../../../types/app/blogTypes";
import "./blog.scss";

type BlogPost = UIBlogPost;
type BlogTab = "all" | "published" | "draft" | "trash";
type StatusCounts = Record<BlogTab, number>;

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const EMPTY_COUNTS: StatusCounts = { all: 0, published: 0, draft: 0, trash: 0 };
const statusForTab: Record<Exclude<BlogTab, "all">, BlogPostStatus> = {
  published: "published", draft: "draft", trash: "trashed",
};

const BlogManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<BlogTab>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategoryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quickEditId, setQuickEditId] = useState<string | null>(null);
  const [quickEditError, setQuickEditError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [statusCounts, setStatusCounts] = useState<StatusCounts>(EMPTY_COUNTS);

  const loadCategories = useCallback(async () => {
    try {
      const data = await BlogService.getBlogCategories(false);
      setCategories(Array.isArray(data) ? data : []);
    } catch { setCategories([]); }
  }, []);

  const loadStatusCounts = useCallback(async () => {
    try {
      // totalNumberOfRecords represents all matching rows, not just this one-row request.
      const [all, published, draft, trash] = await Promise.all([
        BlogService.getBlogPosts(1, 1),
        BlogService.getBlogPosts(1, 1, "published"),
        BlogService.getBlogPosts(1, 1, "draft"),
        BlogService.getBlogPosts(1, 1, "trashed"),
      ]);
      const serverCounts = all.statusCounts;
      setStatusCounts({
        // The unfiltered request is the authoritative All total. Do not add
        // status responses here: APIs that ignore an unsupported status filter
        // would otherwise multiply the same total (for example, 17 into 51).
        all: serverCounts?.all ?? all.totalNumberOfRecords,
        published: serverCounts?.published ?? published.totalNumberOfRecords,
        draft: serverCounts?.draft ?? draft.totalNumberOfRecords,
        trash: serverCounts?.trashed ?? trash.totalNumberOfRecords,
      });
    } catch {
      // Keep the most recently confirmed totals when a background count refresh fails.
    }
  }, []);

  const loadBlogData = useCallback(async (page: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await BlogService.getBlogPosts(page, pageSize, activeTab === "all" ? undefined : statusForTab[activeTab]);
      const fallbackStatus = activeTab === "all" ? undefined : statusForTab[activeTab];
      setPosts(response.results.map((post) => adaptBlogPostFromApi(post, fallbackStatus)));
      setCurrentPage(Math.max(response.pageNumber - 1, 0));
      // Some API responses can retain a stale totalNumberOfPages value after
      // PageSize changes. Derive it from the authoritative record total too.
      setTotalPages(Math.max(
        response.totalNumberOfPages || 0,
        Math.ceil(response.totalNumberOfRecords / pageSize),
        1,
      ));
      setTotalRecords(response.totalNumberOfRecords);
      setSelectedIds([]);
      setQuickEditId(null);
    } catch {
      setError("Failed to load blog posts. Please try again.");
    } finally { setLoading(false); }
  }, [activeTab, pageSize]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => {
    loadBlogData(currentPage + 1);
    loadStatusCounts();
  }, [activeTab, currentPage, loadBlogData, loadStatusCounts]);

  const visiblePosts = useMemo(() => {
    const filtered = posts.filter((post) => {
      const search = searchTerm.toLowerCase();
      const matchesSearch = !search || post.title.toLowerCase().includes(search) || post.excerpt.toLowerCase().includes(search) || post.author.name.toLowerCase().includes(search);
      return matchesSearch && (!categoryFilter || post.categoryIds?.includes(categoryFilter));
    });
    if (!sortColumn) return filtered;
    return [...filtered].sort((a, b) => {
      const values: Record<string, [string | Date, string | Date]> = {
        title: [a.title.toLowerCase(), b.title.toLowerCase()],
        status: [a.status || "published", b.status || "published"],
        category: [a.categoryIds?.[0] || "", b.categoryIds?.[0] || ""],
        author: [a.author.name.toLowerCase(), b.author.name.toLowerCase()],
        date: [new Date(a.publishDate), new Date(b.publishDate)],
      };
      const [aValue, bValue] = values[sortColumn] || ["", ""];
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [posts, searchTerm, categoryFilter, sortColumn, sortDirection]);

  const categoryIds = useMemo(() => Array.from(new Set(posts.flatMap((post) => post.categoryIds || []))).sort(), [posts]);
  const categoryName = (id: string) => categories.find((category) => category.id === id)?.name || id;
  const refresh = async () => { await Promise.all([loadBlogData(currentPage + 1), loadStatusCounts()]); };

  const handleUpdatePost = async (updatedPost: Partial<BlogPost>) => {
    try {
      if (!updatedPost.id) return;
      const original = posts.find((post) => post.id === updatedPost.id);
      if (!original) return;
      const apiPost = await BlogService.getBlogPostBySlug(original.slug);
      await BlogService.updateBlogPost(updatedPost.id, adaptBlogPostToUpdateCommand({ ...adaptBlogPostFromApi(apiPost, original.status), ...updatedPost } as UIBlogPost));
      await refresh();
    } catch (err: any) { setQuickEditError(err?.response?.data?.message || err?.message || "Failed to update post. Please try again."); }
  };

  const handleTrashPost = async (id: string) => {
    try { await BlogService.trashBlogPost(id); await refresh(); }
    catch { setQuickEditError("Failed to move post to trash. Please try again."); }
  };
  const handleRestorePost = async (id: string) => {
    try { await BlogService.restoreBlogPost(id); await refresh(); }
    catch { setQuickEditError("Failed to restore post. Please try again."); }
  };
  const handleConfirmDelete = async () => {
    if (!postToDelete) return;
    try {
      await BlogService.deleteBlogPost(postToDelete);
      if (posts.length === 1 && currentPage > 0) setCurrentPage((page) => page - 1);
      else await refresh();
      setPostToDelete(null); setShowDeleteModal(false); setShowDeleteSuccess(true);
    } catch { setQuickEditError("Failed to delete post. Please try again."); }
  };
  const handleSort = (column: string) => {
    if (sortColumn === column) setSortDirection((direction) => direction === "asc" ? "desc" : "asc");
    else { setSortColumn(column); setSortDirection("asc"); }
  };
  const renderHeader = (column: string, label: string, className: string) => <th className={`${className} sortable-header`} onClick={() => handleSort(column)}><div className="header-content"><span>{label}</span>{sortColumn === column && <span className="sort-icons">{sortDirection === "asc" ? "▲" : "▼"}</span>}</div></th>;
  const firstRecord = totalRecords === 0 ? 0 : currentPage * pageSize + 1;
  const lastRecord = Math.min((currentPage + 1) * pageSize, totalRecords);

  return <>
    <InAppTemplate pageHeadingTitle="Blog Management" childComponent={<div className="blog-management">
      <div className="blog-header"><h1>Posts</h1><button className="btn-add-post" onClick={() => navigate("/app/blog/new")}><FiPlus /> Add Post</button></div>
      <div className="blog-tabs-container">
        <div className="tabs-and-search">
          <Nav variant="tabs" className="table_navs">{(["all", "published", "draft", "trash"] as BlogTab[]).map((tab) => <Nav.Item key={tab}><Nav.Link eventKey={tab} data-active={activeTab === tab} onClick={() => { setActiveTab(tab); setCurrentPage(0); }}><div className="table_nav_item">{tab === "all" ? "All" : tab === "trash" ? "Trash" : `${tab[0].toUpperCase()}${tab.slice(1)}`} ({statusCounts[tab]})</div><div className="underline" /></Nav.Link></Nav.Item>)}</Nav>
          <div className="search-filter"><div className="search-box"><BiSearch size={20} color="#BDBDBD" /><input type="text" className="search-input" placeholder="Search blog" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} /></div><div className="filter-btn-wrapper"><select className="filter-btn" value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}><option value="">Filter by</option>{categoryIds.map((id) => <option key={id} value={id}>{categoryName(id)}</option>)}</select></div></div>
        </div>
        {loading ? <div className="loading-container"><LoadingItem /></div> : error ? <div className="error-container"><AlertMsg type="error" message={error} /><button onClick={() => loadBlogData(currentPage + 1)} className="btn btn-primary">Retry</button></div> : <>
          <div className="blog-table-container"><table className="blog-table"><thead><tr><th className="col-checkbox"><input type="checkbox" checked={visiblePosts.length > 0 && selectedIds.length === visiblePosts.length} onChange={(event) => setSelectedIds(event.target.checked ? visiblePosts.map((post) => post.id) : [])} /></th>{renderHeader("title", "Title", "col-title")}{renderHeader("status", "Status", "col-status")}{renderHeader("category", "Category", "col-category")}{renderHeader("author", "Author", "col-author")}{renderHeader("date", "Last Modified", "col-date")}</tr></thead><tbody>{visiblePosts.length === 0 ? <tr><td colSpan={6} className="no-posts">No {activeTab === "all" ? "posts" : `${activeTab === "trash" ? "trashed" : activeTab} posts`} found</td></tr> : visiblePosts.map((post) => <BlogPostRow key={post.id} post={post} isSelected={selectedIds.includes(post.id)} onSelect={(id) => setSelectedIds((ids) => ids.includes(id) ? ids.filter((postId) => postId !== id) : [...ids, id])} onQuickEdit={(id) => setQuickEditId((current) => current === id ? null : id)} onUpdatePost={handleUpdatePost} onTrash={handleTrashPost} onRestore={handleRestorePost} onDeletePermanently={(id) => { setPostToDelete(id); setShowDeleteModal(true); }} isQuickEditOpen={quickEditId === post.id} />)}</tbody></table></div>
          <div className="blog-pagination-footer"><label className="rows-per-page">Rows per page: <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setCurrentPage(0); }} aria-label="Rows per page">{PAGE_SIZE_OPTIONS.map((size) => <option key={size} value={size}>{size}</option>)}</select></label><span className="pagination-summary">Showing {firstRecord}–{lastRecord} of {totalRecords}</span>{totalPages > 1 && <ReactPaginate breakLabel="..." disableInitialCallback disabledClassName="disabled" disabledLinkClassName="disabled-link" nextLabel=">" nextAriaLabel="Go to next page" onPageChange={({ selected }) => setCurrentPage(selected)} pageRangeDisplayed={4} forcePage={currentPage} className="pagination_items" pageClassName="page_num" pageLinkClassName="page_link" activeClassName="active_page_link" previousClassName="previous_page_link" nextClassName="next_page_link" pageCount={totalPages} previousLabel="<" previousAriaLabel="Go to previous page" renderOnZeroPageCount={null} />}</div>
        </>}
      </div>
    </div>} />
    <ConfirmationModal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} title="Delete Post Permanently" message="Are you sure you want to permanently delete this post? This action cannot be undone." confirmText="Delete Permanently" cancelText="Cancel" onConfirm={handleConfirmDelete} variant="danger" />
    <ErrorModal show={!!quickEditError} onHide={() => setQuickEditError("")} title="Blog Post Error" message={quickEditError} />
    <SuccessModal show={showDeleteSuccess} onHide={() => setShowDeleteSuccess(false)} title="Post Deleted" message="The blog post has been permanently deleted." />
  </>;
};

export default BlogManagement;
